import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import {
    Button,
    CircularProgress,
    TextField,
    IconButton,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    ListSubheader,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import CodeIcon from '@material-ui/icons/Code';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ViewStreamIcon from '@material-ui/icons/ViewStream';
import { Dialog } from 'components/shared/Dialog';
import {
    REQUIREMENT_LEVELS,
    UI_COMPONENT_TYPES,
    SELECT_TYPES,
    FILE_UPLOAD_TYPES,
    INTERNAL_FIELD_TYPES,
    INTERNAL_FIELD_TYPES_GROUPED,
    INTERNAL_FIELD_TYPES_BY_SECTION,
    INTERNAL_FIELD_TYPE_TO_FIELD_NAME,
    INTERNAL_FIELD_TYPE_TO_LABEL,
    FREE_INTERNAL_TYPES,
    TASK_TYPES,
    SECTION_TYPES,
    PORTAL_CONFIG_RULES,
    STATIC_OPTIONS_MAP_NAMES,
    CONFIG_DESCRIPTIONS,
} from './dynamicConfigConstants';
import {
    runRules,
    mergeIssues,
    flattenIssues,
    getAllowedSectionTypesForTask,
    getAllowedUiComponentsForSection,
    getForcedFieldValues,
    getAllowedFieldInternalTypesForTask,
} from './portalConfigRulesEngine';
import JsonEditor from '../../../shared/JsonEditor/JsonEditor';

const emptyField = () => ({ tempId: Date.now() + Math.random(), label: '', name: '', requirementLevel: 'MANDATORY', internalFieldType: '', order: 0, uicomponentType: 'INPUT_FIELD_TEXT', configurations: [] });
const emptySection = () => ({ tempId: Date.now() + Math.random(), label: '', order: 1, configurations: [], fields: [emptyField()] });
const emptyTask = () => ({ tempId: Date.now() + Math.random(), name: '', description: '', order: 1, requirementLevel: 'OPTIONAL', type: 'APPLICATION_FORM_DATA_CONFIGURABLE', configurations: [], sections: [emptySection()] });

// ── Validation ─────────────────────────────────────────────────────────────

const getCfgVal = (configurations, key) => {
    const entry = (configurations || []).find(c => c.key === key);
    return entry !== undefined ? entry.value : undefined;
};

const applyConfig = (configurations, key, value) => {
    const existing = [...(configurations || [])];
    const idx = existing.findIndex(c => c.key === key);
    if (value === '' || value === undefined || value === null) {
        if (idx !== -1) existing.splice(idx, 1);
    } else {
        if (idx !== -1) existing[idx] = { ...existing[idx], value: String(value) };
        else existing.push({ key, value: String(value) });
    }
    return existing;
};

const validateFieldConfigs = (field) => {
    const errs = {};
    const get = (key) => getCfgVal(field.configurations, key);
    const uiType = field.uicomponentType;

    const readOnly = get('readOnly');
    if (FILE_UPLOAD_TYPES.includes(uiType)) {
        if (readOnly !== undefined) errs.readOnly = "readOnly is not supported for file upload fields";
    } else if (readOnly !== undefined && !['true', 'false'].includes(readOnly.toLowerCase())) {
        errs.readOnly = "readOnly must be 'true' or 'false'";
    }
    const helperText = get('helperText');
    if (helperText !== undefined && !helperText.trim()) {
        errs.helperText = "helperText cannot be empty if set";
    }
    if (uiType === 'INPUT_FIELD_TEXT') {
        const fieldRows = get('fieldRows');
        if (fieldRows !== undefined) {
            const n = parseInt(fieldRows, 10);
            if (isNaN(n) || n <= 0) errs.fieldRows = "fieldRows must be a positive integer";
        }
    }
    if (uiType === 'INPUT_FIELD_SELECT') {
        const staticMap = get('uiStaticOptionsMapName');
        if (!staticMap) {
            errs.uiStaticOptionsMapName = `Required. Must be one of: ${STATIC_OPTIONS_MAP_NAMES.join(', ')}`;
        } else if (!STATIC_OPTIONS_MAP_NAMES.includes(staticMap)) {
            errs.uiStaticOptionsMapName = `Must be one of: ${STATIC_OPTIONS_MAP_NAMES.join(', ')}`;
        }
    }
    if (uiType === 'INPUT_FIELD_SELECT_CUSTOM') {
        const selectOption = get('selectOption');
        const staticMap = get('uiStaticOptionsMapName');
        if (selectOption !== undefined) {
            const parts = selectOption.split(',').map(p => p.trim()).filter(Boolean);
            if (parts.length === 0) errs.selectOption = "selectOption must contain at least one non-empty option";
        }
        if (staticMap !== undefined && !staticMap.trim()) {
            errs.uiStaticOptionsMapName = "uiStaticOptionsMapName cannot be empty";
        }
        if (selectOption === undefined && staticMap === undefined) {
            errs._select = "SELECT fields require either selectOption or uiStaticOptionsMapName";
        }
    }
    if (['CHECKBOX', 'RADIO_GROUP'].includes(uiType)) {
        const selectOption = get('selectOption');
        if (selectOption === undefined) {
            errs.selectOption = `selectOption is required for ${uiType} fields`;
        } else {
            const parts = selectOption.split(',').map(p => p.trim()).filter(Boolean);
            if (parts.length === 0) errs.selectOption = "selectOption must contain at least one non-empty option";
        }
    }
    if (uiType === 'SIGNATURE') {
        const signatureText = get('signatureText');
        if (signatureText !== undefined && !signatureText.trim()) errs.signatureText = "signatureText cannot be empty if set";
        const authText = get('authorizationText');
        if (authText !== undefined && !authText.trim()) errs.authorizationText = "authorizationText cannot be empty if set";
    }
    if (FILE_UPLOAD_TYPES.includes(uiType)) {
        const acceptedFileTypes = get('acceptedFileTypes');
        if (acceptedFileTypes !== undefined) {
            const parts = acceptedFileTypes.split(',').map(p => p.trim()).filter(Boolean);
            if (parts.length === 0 || parts.some(p => !p.startsWith('.') && !p.includes('/'))) {
                errs.acceptedFileTypes = "Use extensions (.pdf) or MIME types (image/png)";
            }
        }
        const maxFiles = get('maxNumberOfFiles');
        if (maxFiles !== undefined) {
            const n = parseInt(maxFiles, 10);
            if (isNaN(n) || n <= 0) errs.maxNumberOfFiles = "maxNumberOfFiles must be a positive integer";
        }
    }
    return errs;
};

const validateSectionConfigs = (section) => {
    const errs = {};
    const get = (key) => getCfgVal(section.configurations, key);

    const maxOwners = get('maxOwners');
    if (maxOwners !== undefined) {
        const n = parseInt(maxOwners, 10);
        if (isNaN(n) || n < 1 || n > 10) errs.maxOwners = "Must be an integer between 1 and 10";
    }
    const hideSectionInUi = get('hideSectionInUi');
    if (hideSectionInUi !== undefined && !['true', 'false'].includes(hideSectionInUi.toLowerCase())) {
        errs.hideSectionInUi = "hideSectionInUi must be 'true' or 'false'";
    }
    const flagName = get('activationFlagName');
    const flagValue = get('activationFlagValue');
    if (flagName !== undefined && !flagName.trim()) errs.activationFlagName = "activationFlagName cannot be empty";
    if (flagValue !== undefined && !flagValue.trim()) errs.activationFlagValue = "activationFlagValue cannot be empty";
    if (flagName !== undefined && !errs.activationFlagName && flagValue === undefined) {
        errs.activationFlagValue = "Required when activationFlagName is set";
    }
    if (flagValue !== undefined && !errs.activationFlagValue && flagName === undefined) {
        errs.activationFlagName = "Required when activationFlagValue is set";
    }
    const industrySpecific = get('industrySpecific');
    if (industrySpecific !== undefined && !['true', 'false'].includes(industrySpecific.toLowerCase())) {
        errs.industrySpecific = "industrySpecific must be 'true' or 'false'";
    }
    return errs;
};

const validateFieldData = (field, allFields, fi) => {
    const err = {};
    if (!field.label?.trim()) err.label = "Field label is required";
    if (!field.name?.trim()) {
        err.name = "Field name is required";
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(field.name)) {
        err.name = "Must start with a letter and contain only letters, numbers, or underscores";
    } else if (allFields.some((f, i) => i !== fi && f.name === field.name)) {
        err.name = "Duplicate field name in this section";
    }
    if (field.order === undefined || field.order === null || Number(field.order) < 0) {
        err.order = "Order must be 0 or greater";
    }
    if (!UI_COMPONENT_TYPES.includes(field.uicomponentType)) err.uicomponentType = "Invalid UI component type";
    if (!REQUIREMENT_LEVELS.includes(field.requirementLevel)) err.requirementLevel = "Invalid requirement level";
    const cfgErrs = validateFieldConfigs(field);
    if (Object.keys(cfgErrs).length > 0) err.configurations = cfgErrs;
    return err;
};

const validateSectionData = (section, allSections, si) => {
    const err = {};
    if (!section.label?.trim()) err.label = "Section label is required";
    if (!section.order || Number(section.order) < 1) {
        err.order = "Order must be at least 1";
    } else if (allSections.some((s, i) => i !== si && s.order === section.order)) {
        err.order = "Duplicate order value";
    }
    if (!section.fields || section.fields.length === 0) err.fields = "Section must have at least one field";
    const fieldsMap = {};
    (section.fields || []).forEach((f, fi) => {
        const fErr = validateFieldData(f, section.fields, fi);
        if (Object.keys(fErr).length > 0) fieldsMap[fi] = fErr;
    });
    if (Object.keys(fieldsMap).length > 0) err.fields_map = fieldsMap;
    const cfgErrs = validateSectionConfigs(section);
    if (Object.keys(cfgErrs).length > 0) err.configurations = cfgErrs;
    return err;
};

const validateTaskData = (task, allTasks, ti) => {
    const err = {};
    if (!task.name?.trim()) err.name = "Task name is required";
    if (!task.order || Number(task.order) < 1) {
        err.order = "Order must be at least 1";
    } else if (allTasks.some((t, i) => i !== ti && t.order === task.order)) {
        err.order = "Duplicate order value";
    }
    if (!TASK_TYPES.includes(task.type)) err.type = "Invalid task type";
    if (!REQUIREMENT_LEVELS.includes(task.requirementLevel)) err.requirementLevel = "Invalid requirement level";
    if (task.type !== 'PLAID_DATA_CONFIGURABLE' && (!task.sections || task.sections.length === 0)) err.sections = "Task must have at least one section";
    const sectionsMap = {};
    (task.sections || []).forEach((s, si) => {
        const sErr = validateSectionData(s, task.sections, si);
        if (Object.keys(sErr).length > 0) sectionsMap[si] = sErr;
    });
    if (Object.keys(sectionsMap).length > 0) err.sections_map = sectionsMap;
    return err;
};

const validateAll = (sectionList, taskList) => {
    const errs = { sections: {}, tasks: {} };
    let hasErrors = false;
    sectionList.forEach((section, si) => {
        const sErr = validateSectionData(section, sectionList, si);
        if (Object.keys(sErr).length > 0) { errs.sections[si] = sErr; hasErrors = true; }
    });
    taskList.forEach((task, ti) => {
        const tErr = validateTaskData(task, taskList, ti);
        if (Object.keys(tErr).length > 0) { errs.tasks[ti] = tErr; hasErrors = true; }
    });
    return { errs, hasErrors };
};

// ── Display sub-components ─────────────────────────────────────────────────

const RequirementChip = ({ level }) => {
    const colors = { MANDATORY: { bg: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }, OPTIONAL: { bg: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }, HIDDEN: { bg: 'var(--bg-canvas)', color: 'var(--fg-tertiary)' } };
    const style = colors[level] || colors.OPTIONAL;
    return <Chip label={level} size="small" style={{ backgroundColor: style.bg, color: style.color, fontSize: '0.7rem', height: '20px' }} />;
};

const FieldsTable = ({ fields }) => {
    if (!fields || fields.length === 0) return null;
    const sorted = [...fields].sort((a, b) => a.order - b.order);
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
                <tr style={{ borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#aaa', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: '36px' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#aaa', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Label</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#aaa', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Field Name</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#aaa', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Requirement</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#aaa', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>UI Type</th>
                </tr>
            </thead>
            <tbody>
                {sorted.map((field, i) => (
                    <tr key={field.id || i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '8px 10px', color: '#bbb', fontSize: '0.75rem' }}>{field.order}</td>
                        <td style={{ padding: '8px 10px', fontWeight: 500, color: '#333' }}>{field.label}</td>
                        <td style={{ padding: '8px 10px' }}>
                            <code style={{ backgroundColor: '#eff3ff', color: 'var(--rox-lilac-ink)', padding: '2px 7px', borderRadius: '4px', fontSize: '0.78rem', fontFamily: 'monospace' }}>{field.name}</code>
                        </td>
                        <td style={{ padding: '8px 10px' }}><RequirementChip level={field.requirementLevel} /></td>
                        <td style={{ padding: '8px 10px' }}>
                            <span style={{ fontSize: '0.7rem', color: '#777', backgroundColor: 'var(--bg-canvas)', padding: '2px 7px', borderRadius: '4px', whiteSpace: 'nowrap' }}>{field.uicomponentType}</span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const SectionDisplay = ({ section, index }) => {
    const [expanded, setExpanded] = useState(index === 0);
    return (
        <div style={{ marginBottom: '10px', borderRadius: '6px', border: '1px solid #e8eaf6', overflow: 'hidden' }}>
            <div
                onClick={() => setExpanded(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', backgroundColor: '#f8f9ff', cursor: 'pointer', userSelect: 'none', borderBottom: expanded ? '1px solid #e8eaf6' : 'none' }}
            >
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--rox-lilac-ink)', backgroundColor: '#e8eaf6', padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                    Order {section.order}
                </span>
                <span style={{ fontWeight: 600, color: '#333', fontSize: '0.88rem' }}>{section.label}</span>
                <span style={{ color: '#aaa', fontSize: '0.78rem', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{section.fields?.length || 0} fields</span>
                <ExpandMoreIcon fontSize="small" style={{ color: '#bbb', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }} />
            </div>
            {expanded && (
                <div style={{ padding: '4px 0' }}>
                    <FieldsTable fields={section.fields} />
                </div>
            )}
        </div>
    );
};

const TaskDisplay = ({ task }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div style={{ marginBottom: '10px', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div
                onClick={() => setExpanded(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '12px 16px', cursor: 'pointer', borderLeft: '4px solid #4caf50', backgroundColor: expanded ? '#f9fbe7' : '#fff', transition: 'background-color 0.15s', userSelect: 'none' }}
            >
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--status-approved)', backgroundColor: 'var(--badge-approved-bg)', padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                    Order {task.order}
                </span>
                <span style={{ fontWeight: 600, color: '#222', fontSize: '0.95rem' }}>{task.name}</span>
                <RequirementChip level={task.requirementLevel} />
                <span style={{ fontSize: '0.68rem', color: '#888', backgroundColor: 'var(--bg-canvas)', padding: '2px 8px', borderRadius: '4px', whiteSpace: 'nowrap' }}>{task.type}</span>
                <ExpandMoreIcon fontSize="small" style={{ marginLeft: 'auto', color: '#bbb', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }} />
            </div>
            {expanded && (
                <div style={{ padding: '16px', backgroundColor: 'var(--bg-canvas)', borderTop: '1px solid #eee' }}>
                    {task.sections && task.sections.length > 0 && (
                        <div>
                            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                Sections ({task.sections.length})
                            </div>
                            {task.sections.sort((a, b) => a.order - b.order).map((section, idx) => (
                                <SectionDisplay key={section.id} section={section} index={idx} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Config editors ─────────────────────────────────────────────────────────

const BoolSelect = ({ label, value, cfgKey, onChange, error }) => (
    <FormControl variant="outlined" size="small" fullWidth error={!!error}>
        <InputLabel>{label}</InputLabel>
        <Select value={value} onChange={(e) => onChange(cfgKey, e.target.value)} label={label}>
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
);

// Known config keys per field UI type — used to separate "custom" configs
const FIELD_KNOWN_KEYS = {
    INPUT_FIELD_TEXT:           ['readOnly', 'helperText', 'fieldRows', 'defaultValue'],
    INPUT_FIELD_SELECT:         ['readOnly', 'helperText', 'uiStaticOptionsMapName'],
    INPUT_FIELD_SELECT_CUSTOM:  ['readOnly', 'helperText', 'selectOption', 'uiStaticOptionsMapName'],
    INPUT_FIELD_SELECT_YES_NO:  ['readOnly', 'helperText'],
    CHECKBOX:                   ['readOnly', 'helperText', 'selectOption'],
    RADIO_GROUP:                ['readOnly', 'helperText', 'selectOption'],
    SIGNATURE:                  ['readOnly', 'helperText', 'signatureText', 'authorizationText'],
    FILE_UPLOAD_FIELD:          ['readOnly', 'helperText', 'acceptedFileTypes', 'maxNumberOfFiles'],
    FILE_UPLOAD_MULTIPLE:       ['readOnly', 'helperText', 'acceptedFileTypes', 'maxNumberOfFiles'],
};
const FIELD_KNOWN_KEYS_DEFAULT = ['readOnly', 'helperText'];
const SECTION_KNOWN_KEYS = ['sectionType', 'maxOwners', 'hideSectionInUi', 'industrySpecific', 'activationFlagName', 'activationFlagValue'];

const CustomConfigs = ({ configurations, onChangeConfigs, knownKeys }) => {
    const custom = (configurations || []).filter(c => !knownKeys.includes(c.key));

    const save = (updated) => {
        const known = (configurations || []).filter(c => knownKeys.includes(c.key));
        onChangeConfigs([...known, ...updated]);
    };

    const add   = ()          => save([...custom, { key: '', value: '' }]);
    const upd   = (i, k, v)   => { const n = [...custom]; n[i] = { ...n[i], [k]: v }; save(n); };
    const del   = (i)         => save(custom.filter((_, idx) => idx !== i));

    return (
        <div style={{ gridColumn: '1 / -1', marginTop: '4px' }}>
            <Divider style={{ margin: '6px 0 8px' }} />
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Custom Configurations
            </div>
            {custom.map((cfg, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                    <TextField
                        label="Key" value={cfg.key}
                        onChange={(e) => upd(i, 'key', e.target.value)}
                        size="small" variant="outlined" style={{ flex: 1 }}
                    />
                    <TextField
                        label="Value" value={cfg.value}
                        onChange={(e) => upd(i, 'value', e.target.value)}
                        size="small" variant="outlined" style={{ flex: 2 }}
                    />
                    <IconButton size="small" onClick={() => del(i)} color="secondary">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </div>
            ))}
            <Button startIcon={<AddIcon />} onClick={add} size="small" color="primary">
                Add Configuration
            </Button>
        </div>
    );
};

const ConfigDesc = ({ cfgKey }) => {
    const text = CONFIG_DESCRIPTIONS[cfgKey];
    if (!text) return null;
    return <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px', lineHeight: 1.4 }}>{text}</div>;
};

const FieldConfigurations = ({ configurations, onChangeConfigs, uiType, configErrors = {} }) => {
    const get = (key) => getCfgVal(configurations, key) ?? '';
    const set = (key, value) => onChangeConfigs(applyConfig(configurations, key, value));
    const hasConfigErrors = Object.keys(configErrors).length > 0;
    const [open, setOpen] = useState(hasConfigErrors);
    const knownKeys = FIELD_KNOWN_KEYS[uiType] || FIELD_KNOWN_KEYS_DEFAULT;

    return (
        <div style={{ gridColumn: '1 / -1', marginTop: '4px' }}>
            <Divider style={{ margin: '4px 0 6px' }} />
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
                style={{
                    display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0 6px',
                    color: hasConfigErrors ? 'var(--status-declined)' : '#999',
                }}
            >
                <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Configurations
                </span>
                <ExpandMoreIcon fontSize="small" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s', marginLeft: 'auto' }} />
            </button>
            {open && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                {/* Common */}
                {!FILE_UPLOAD_TYPES.includes(uiType) && (
                    <div>
                        <BoolSelect label="Read Only" cfgKey="readOnly" value={get('readOnly')} onChange={set} error={configErrors.readOnly} />
                        <ConfigDesc cfgKey="readOnly" />
                    </div>
                )}
                <div>
                    <TextField
                        label="Helper Text"
                        value={get('helperText')}
                        onChange={(e) => set('helperText', e.target.value)}
                        size="small" variant="outlined" fullWidth
                        error={!!configErrors.helperText}
                        helperText={configErrors.helperText}
                    />
                    <ConfigDesc cfgKey="helperText" />
                </div>

                {/* TEXT */}
                {uiType === 'INPUT_FIELD_TEXT' && <>
                    <div>
                        <TextField
                            label="Field Rows"
                            type="number"
                            value={get('fieldRows')}
                            onChange={(e) => set('fieldRows', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            inputProps={{ min: 1 }}
                            error={!!configErrors.fieldRows}
                            helperText={configErrors.fieldRows}
                        />
                        <ConfigDesc cfgKey="fieldRows" />
                    </div>
                    <div>
                        <TextField
                            label="Default Value"
                            value={get('defaultValue')}
                            onChange={(e) => set('defaultValue', e.target.value)}
                            size="small" variant="outlined" fullWidth
                        />
                        <ConfigDesc cfgKey="defaultValue" />
                    </div>
                </>}

                {/* INPUT_FIELD_SELECT */}
                {uiType === 'INPUT_FIELD_SELECT' && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <FormControl variant="outlined" size="small" fullWidth error={!!configErrors.uiStaticOptionsMapName}>
                            <InputLabel>Static Options Map Name *</InputLabel>
                            <Select value={get('uiStaticOptionsMapName')} onChange={(e) => set('uiStaticOptionsMapName', e.target.value)} label="Static Options Map Name *">
                                <MenuItem value="">Select…</MenuItem>
                                {STATIC_OPTIONS_MAP_NAMES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                            </Select>
                            {configErrors.uiStaticOptionsMapName && <FormHelperText>{configErrors.uiStaticOptionsMapName}</FormHelperText>}
                        </FormControl>
                        <ConfigDesc cfgKey="uiStaticOptionsMapName" />
                    </div>
                )}

                {/* INPUT_FIELD_SELECT_CUSTOM */}
                {uiType === 'INPUT_FIELD_SELECT_CUSTOM' && <>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <TextField
                            label="Options (comma-separated)"
                            placeholder="Option A, Option B, Option C"
                            value={get('selectOption')}
                            onChange={(e) => set('selectOption', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.selectOption || !!configErrors._select}
                            helperText={configErrors.selectOption || configErrors._select}
                        />
                        <ConfigDesc cfgKey="selectOption" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <TextField
                            label="Static Options Map Name"
                            value={get('uiStaticOptionsMapName')}
                            onChange={(e) => set('uiStaticOptionsMapName', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.uiStaticOptionsMapName}
                            helperText={configErrors.uiStaticOptionsMapName || (get('selectOption') && get('uiStaticOptionsMapName') ? 'Takes precedence over options list' : undefined)}
                        />
                        <ConfigDesc cfgKey="uiStaticOptionsMapName" />
                    </div>
                </>}

                {/* CHECKBOX / RADIO_GROUP */}
                {['CHECKBOX', 'RADIO_GROUP'].includes(uiType) && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <TextField
                            label="Options (comma-separated) *"
                            placeholder="Option A, Option B, Option C"
                            value={get('selectOption')}
                            onChange={(e) => set('selectOption', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.selectOption}
                            helperText={configErrors.selectOption}
                        />
                        <ConfigDesc cfgKey="selectOption" />
                    </div>
                )}

                {/* SIGNATURE */}
                {uiType === 'SIGNATURE' && <>
                    <div>
                        <TextField
                            label="Signature Text"
                            value={get('signatureText')}
                            onChange={(e) => set('signatureText', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.signatureText}
                            helperText={configErrors.signatureText}
                        />
                        <ConfigDesc cfgKey="signatureText" />
                    </div>
                    <div>
                        <TextField
                            label="Authorization Text"
                            value={get('authorizationText')}
                            onChange={(e) => set('authorizationText', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.authorizationText}
                            helperText={configErrors.authorizationText}
                        />
                        <ConfigDesc cfgKey="authorizationText" />
                    </div>
                </>}

                {/* FILE_UPLOAD_FIELD / FILE_UPLOAD_MULTIPLE */}
                {FILE_UPLOAD_TYPES.includes(uiType) && <>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <TextField
                            label="Accepted File Types (e.g. .pdf, image/png)"
                            placeholder=".pdf, .xlsx, image/png"
                            value={get('acceptedFileTypes')}
                            onChange={(e) => set('acceptedFileTypes', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.acceptedFileTypes}
                            helperText={configErrors.acceptedFileTypes}
                        />
                        <ConfigDesc cfgKey="acceptedFileTypes" />
                    </div>
                    <div>
                        <TextField
                            label="Max Number of Files"
                            type="number"
                            value={get('maxNumberOfFiles')}
                            onChange={(e) => set('maxNumberOfFiles', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            inputProps={{ min: 1 }}
                            error={!!configErrors.maxNumberOfFiles}
                            helperText={configErrors.maxNumberOfFiles}
                        />
                        <ConfigDesc cfgKey="maxNumberOfFiles" />
                    </div>
                </>}

                <CustomConfigs
                    configurations={configurations}
                    onChangeConfigs={onChangeConfigs}
                    knownKeys={knownKeys}
                />
            </div>}
        </div>
    );
};

const SectionConfigurations = ({ configurations, onChangeConfigs, configErrors = {}, taskType = '', sectionTypeWarning = '' }) => {
    const get = (key) => getCfgVal(configurations, key) ?? '';
    const set = (key, value) => onChangeConfigs(applyConfig(configurations, key, value));

    const taskSectionTypeRule = getAllowedSectionTypesForTask(taskType);
    const allowedSectionTypes = (taskSectionTypeRule?.level === 'error' && taskSectionTypeRule.values.length > 0)
        ? taskSectionTypeRule.values
        : SECTION_TYPES;

    const hasExtraConfigErrors = !!(configErrors.maxOwners || configErrors.hideSectionInUi || configErrors.industrySpecific || configErrors.activationFlagName || configErrors.activationFlagValue);
    const [openExtra, setOpenExtra] = useState(hasExtraConfigErrors);

    return (
        <div style={{ marginBottom: '16px' }}>
            {/* Section Type — always visible */}
            <div style={{ marginBottom: '8px' }}>
                <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Section Type</InputLabel>
                    <Select value={get('sectionType')} onChange={(e) => set('sectionType', e.target.value)} label="Section Type">
                        <MenuItem value="">Not set</MenuItem>
                        {allowedSectionTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                    <WarnText message={sectionTypeWarning} />
                </FormControl>
                <ConfigDesc cfgKey="sectionType" />
            </div>

            {/* Extra configs — collapsible */}
            <Divider style={{ margin: '0 0 6px' }} />
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenExtra(v => !v); }}
                style={{
                    display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0 6px',
                    color: hasExtraConfigErrors ? 'var(--status-declined)' : '#999',
                }}
            >
                <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Advanced Configurations
                </span>
                <ExpandMoreIcon fontSize="small" style={{ transform: openExtra ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s', marginLeft: 'auto' }} />
            </button>
            {openExtra && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {get('sectionType') === 'OWNER' && (
                        <div>
                            <TextField
                                label="Max Owners"
                                type="number"
                                value={get('maxOwners')}
                                onChange={(e) => set('maxOwners', e.target.value)}
                                size="small" variant="outlined" fullWidth
                                inputProps={{ min: 1, max: 10 }}
                                error={!!configErrors.maxOwners}
                                helperText={configErrors.maxOwners}
                            />
                            <ConfigDesc cfgKey="maxOwners" />
                        </div>
                    )}
                    <div>
                        <BoolSelect label="Hide Section in UI" cfgKey="hideSectionInUi" value={get('hideSectionInUi')} onChange={set} error={configErrors.hideSectionInUi} />
                        <ConfigDesc cfgKey="hideSectionInUi" />
                    </div>
                    <div>
                        <BoolSelect label="Industry Specific" cfgKey="industrySpecific" value={get('industrySpecific')} onChange={set} error={configErrors.industrySpecific} />
                        <ConfigDesc cfgKey="industrySpecific" />
                    </div>
                    <div>
                        <TextField
                            label="Activation Flag Name"
                            value={get('activationFlagName')}
                            onChange={(e) => set('activationFlagName', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.activationFlagName}
                            helperText={configErrors.activationFlagName}
                        />
                        <ConfigDesc cfgKey="activationFlagName" />
                    </div>
                    <div>
                        <TextField
                            label="Activation Flag Value"
                            value={get('activationFlagValue')}
                            onChange={(e) => set('activationFlagValue', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!configErrors.activationFlagValue}
                            helperText={configErrors.activationFlagValue}
                        />
                        <ConfigDesc cfgKey="activationFlagValue" />
                    </div>
                    <CustomConfigs
                        configurations={configurations}
                        onChangeConfigs={onChangeConfigs}
                        knownKeys={SECTION_KNOWN_KEYS}
                    />
                </div>
            )}
        </div>
    );
};

// ── Edit sub-components ────────────────────────────────────────────────────

const WarnText = ({ message }) =>
    message ? <div style={{ color: '#e65100', fontSize: '0.73rem', marginTop: '2px' }}>⚠ {message}</div> : null;

const FieldForm = ({ field, onChange, onRemove, index, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd, fieldErrors = {}, fieldWarnings = {}, sectionType = '', taskType = '' }) => {
    const [internalTypeSearch, setInternalTypeSearch] = useState('');
    const set = (key, val) => onChange({ ...field, [key]: val });
    const setInternalFieldType = (val) => {
        const mapped = INTERNAL_FIELD_TYPE_TO_FIELD_NAME[val];
        const suggestedLabel = INTERNAL_FIELD_TYPE_TO_LABEL[val];
        const isFree = !val || FREE_INTERNAL_TYPES.includes(val);
        onChange({
            ...field,
            internalFieldType: val,
            name: (!isFree && mapped) ? mapped : field.name,
            label: (!field.label && suggestedLabel) ? suggestedLabel : field.label,
        });
    };
    const isNameLocked = !!(
        field.internalFieldType &&
        !FREE_INTERNAL_TYPES.includes(field.internalFieldType) &&
        INTERNAL_FIELD_TYPE_TO_FIELD_NAME[field.internalFieldType]
    );

    // Task-type forced values — auto-apply on mount/change and lock the fields
    const forcedValues = getForcedFieldValues(taskType);
    const taskAllowedInternalTypes = getAllowedFieldInternalTypesForTask(taskType);
    useEffect(() => {
        const updates = {};
        // Forced value, or the task's default when the current type isn't allowed
        const targetInternalType = forcedValues?.internalFieldType
            || (taskAllowedInternalTypes?.length && !taskAllowedInternalTypes.includes(field.internalFieldType)
                ? taskAllowedInternalTypes[0]
                : null);
        if (targetInternalType && field.internalFieldType !== targetInternalType) {
            updates.internalFieldType = targetInternalType;
            const mapped = INTERNAL_FIELD_TYPE_TO_FIELD_NAME[targetInternalType];
            if (mapped && !field.name) updates.name = mapped;
            const label = INTERNAL_FIELD_TYPE_TO_LABEL[targetInternalType];
            if (label && !field.label) updates.label = label;
        }
        if (forcedValues?.uicomponentType && field.uicomponentType !== forcedValues.uicomponentType) {
            updates.uicomponentType = forcedValues.uicomponentType;
        }
        if (Object.keys(updates).length) onChange({ ...field, ...updates });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskType]);

    const hasErrors = Object.keys(fieldErrors).length > 0;

    // Filter allowed UI component types based on section rules (error-level = hide, warning-level = keep all)
    const uiComponentRule = getAllowedUiComponentsForSection(sectionType);
    const filteredUiTypes = (uiComponentRule?.level === 'error' && uiComponentRule.values.length > 0)
        ? UI_COMPONENT_TYPES.filter(t => uiComponentRule.values.includes(t))
        : UI_COMPONENT_TYPES;

    const sectionFieldGroups = (sectionType && INTERNAL_FIELD_TYPES_BY_SECTION[sectionType]?.length)
        ? INTERNAL_FIELD_TYPES_BY_SECTION[sectionType]
        : INTERNAL_FIELD_TYPES_GROUPED;
    // Task-level restriction (e.g. upload tasks only accept client portal file types)
    const internalFieldGroups = taskAllowedInternalTypes
        ? [{ group: 'Allowed by task type', values: taskAllowedInternalTypes }]
        : sectionFieldGroups;

    const filteredInternalFieldGroups = internalTypeSearch
        ? internalFieldGroups
            .map(({ group, values }) => ({
                group,
                values: values.filter(v => v.toLowerCase().includes(internalTypeSearch.toLowerCase())),
            }))
            .filter(({ values }) => values.length > 0)
        : internalFieldGroups;
    const fieldNameGroups = PORTAL_CONFIG_RULES.sectionTypeRules[sectionType]?.fieldNames || null;
    return (
        <div
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            style={{
                border: `2px solid ${isDragOver ? 'var(--rox-lilac-ink)' : (hasErrors ? 'var(--status-declined)' : 'var(--neutral-200)')}`,
                borderRadius: '4px',
                padding: '12px',
                backgroundColor: isDragOver ? '#e8eaf6' : 'var(--bg-canvas)',
                cursor: 'grab',
                transition: 'border-color 0.15s, background-color 0.15s',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <DragIndicatorIcon fontSize="small" style={{ color: '#bbb', cursor: 'grab' }} />
                    <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>Field {index + 1}</span>
                </div>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} color="secondary"><DeleteIcon fontSize="small" /></IconButton>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {/* 1. Internal Field Type — drives the rest */}
                <FormControl variant="outlined" size="small" fullWidth style={{ gridColumn: '1 / -1' }} error={!!fieldErrors.internalFieldType}
                    disabled={!!forcedValues?.internalFieldType}>
                    <InputLabel>Internal Field Type{forcedValues?.internalFieldType ? ' (fixed by task type)' : ''}</InputLabel>
                    <Select
                        value={field.internalFieldType}
                        onChange={(e) => setInternalFieldType(e.target.value)}
                        label={`Internal Field Type${forcedValues?.internalFieldType ? ' (fixed by task type)' : ''}`}
                        onClose={() => setInternalTypeSearch('')}
                        MenuProps={{ autoFocus: false }}
                    >
                        <ListSubheader style={{ padding: '8px 12px', backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
                            <TextField
                                autoFocus
                                placeholder="Filter..."
                                value={internalTypeSearch}
                                onChange={(e) => setInternalTypeSearch(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                variant="outlined"
                                fullWidth
                            />
                        </ListSubheader>
                        {!taskAllowedInternalTypes && <MenuItem value="">None</MenuItem>}
                        {filteredInternalFieldGroups.flatMap(({ group, values }) => [
                            <ListSubheader key={group} style={{ fontWeight: 600, fontSize: '0.7rem', color: 'var(--rox-lilac-ink)', lineHeight: '28px', backgroundColor: 'var(--bg-canvas)' }}>{group}</ListSubheader>,
                            ...values.map(t => <MenuItem key={t} value={t} style={{ paddingLeft: '20px', fontSize: '0.85rem' }}>{t}</MenuItem>)
                        ])}
                    </Select>
                    {fieldErrors.internalFieldType && <FormHelperText>{fieldErrors.internalFieldType}</FormHelperText>}
                    <WarnText message={fieldWarnings.internalFieldType} />
                </FormControl>

                {/* 2. Label */}
                <TextField
                    label="Label *" value={field.label}
                    onChange={(e) => set('label', e.target.value)}
                    size="small" variant="outlined" fullWidth
                    error={!!fieldErrors.label} helperText={fieldErrors.label}
                />

                {/* 3. Field Name — adapts based on internalFieldType */}
                {fieldNameGroups && !isNameLocked && !FREE_INTERNAL_TYPES.includes(field.internalFieldType) ? (
                    <FormControl variant="outlined" size="small" fullWidth error={!!fieldErrors.name}>
                        <InputLabel>Field Name *</InputLabel>
                        <Select value={field.name} onChange={(e) => set('name', e.target.value)} label="Field Name *">
                            <MenuItem value="">Select…</MenuItem>
                            {fieldNameGroups.flatMap(({ group, values }) => [
                                <ListSubheader key={group} style={{ fontWeight: 600, fontSize: '0.7rem', color: 'var(--rox-lilac-ink)', lineHeight: '28px', backgroundColor: 'var(--bg-canvas)' }}>{group}</ListSubheader>,
                                ...values.map(n => <MenuItem key={n} value={n} style={{ paddingLeft: '20px', fontSize: '0.85rem' }}>{n}</MenuItem>)
                            ])}
                        </Select>
                        {fieldErrors.name && <FormHelperText>{fieldErrors.name}</FormHelperText>}
                    </FormControl>
                ) : (() => {
                    const expectedName = isNameLocked ? INTERNAL_FIELD_TYPE_TO_FIELD_NAME[field.internalFieldType] : null;
                    const nameMismatch = !!(isNameLocked && expectedName && field.name && field.name !== expectedName);
                    return (
                        <TextField
                            label="Field Name *" value={field.name}
                            onChange={(e) => set('name', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            disabled={isNameLocked && !nameMismatch}
                            error={!!fieldErrors.name}
                            helperText={nameMismatch
                                ? <span style={{ color: '#e65100' }}>⚠ Recommended: '{expectedName}' — <button type="button" onClick={() => set('name', expectedName)} style={{ background: 'none', border: 'none', color: 'var(--rox-lilac-ink)', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}>Use correct name</button></span>
                                : fieldErrors.name}
                        />
                    );
                })()}

                {/* 4. UI Component Type — filtered by section type, locked if task forces it */}
                <FormControl variant="outlined" size="small" fullWidth error={!!fieldErrors.uicomponentType}
                    disabled={!!forcedValues?.uicomponentType}>
                    <InputLabel>UI Component Type{forcedValues?.uicomponentType ? ' (fixed by task type)' : ''}</InputLabel>
                    <Select value={field.uicomponentType} onChange={(e) => set('uicomponentType', e.target.value)} label={`UI Component Type${forcedValues?.uicomponentType ? ' (fixed by task type)' : ''}`}>
                        {filteredUiTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                    {fieldErrors.uicomponentType && <FormHelperText>{fieldErrors.uicomponentType}</FormHelperText>}
                    <WarnText message={fieldWarnings.uicomponentType} />
                </FormControl>

                {/* 5. Requirement Level */}
                <FormControl variant="outlined" size="small" fullWidth error={!!fieldErrors.requirementLevel}>
                    <InputLabel>Requirement Level</InputLabel>
                    <Select value={field.requirementLevel} onChange={(e) => set('requirementLevel', e.target.value)} label="Requirement Level">
                        {REQUIREMENT_LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                    {fieldErrors.requirementLevel && <FormHelperText>{fieldErrors.requirementLevel}</FormHelperText>}
                </FormControl>

                {/* 6. Order */}
                <TextField
                    label="Order" type="number" value={field.order}
                    onChange={(e) => set('order', parseInt(e.target.value) || 0)}
                    size="small" variant="outlined" fullWidth
                    error={!!fieldErrors.order}
                    helperText={fieldErrors.order || (field.order !== index + 1
                        ? <span>Suggested:<button type="button" onClick={() => set('order', index + 1)} style={{ background: 'none', border: 'none', color: 'var(--rox-lilac-ink)', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}>{index + 1}</button></span>
                        : undefined)}
                />
                <FieldConfigurations
                    configurations={field.configurations}
                    onChangeConfigs={(cfgs) => set('configurations', cfgs)}
                    uiType={field.uicomponentType}
                    configErrors={fieldErrors.configurations || {}}
                />
            </div>
        </div>
    );
};

const SectionForm = ({ section, onChange, onRemove, index, showRemove = true, isDragOver = false, isDragging = false, onDragStart, onDragOver, onDrop, onDragEnd, sectionErrors = {}, sectionWarnings = {}, collapseKey = 0, expandKey = 0, taskType = '' }) => {
    const dragIndex = useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [expanded, setExpanded] = useState(true);
    useEffect(() => { if (collapseKey > 0) setExpanded(false); }, [collapseKey]);
    useEffect(() => { if (expandKey > 0) setExpanded(true); }, [expandKey]);

    const set = (key, val) => onChange({ ...section, [key]: val });
    const updateField = (i, f) => { const arr = [...section.fields]; arr[i] = f; onChange({ ...section, fields: arr }); };
    const addField = () => onChange({ ...section, fields: [...section.fields, { ...emptyField(), order: section.fields.length }] });
    const removeField = (i) => onChange({ ...section, fields: section.fields.filter((_, idx) => idx !== i) });
    const sectionType = getCfgVal(section.configurations, 'sectionType') || '';

    const sortedFields = [...(section.fields || [])].sort((a, b) => a.order - b.order);

    const handleDragStart = (e, i) => { dragIndex.current = i; e.dataTransfer.effectAllowed = 'move'; };
    const handleDragOver = (e, i) => { e.preventDefault(); if (dragIndex.current !== i) setDragOverIndex(i); };
    const handleDrop = (e, dropI) => {
        e.preventDefault();
        if (dragIndex.current === null || dragIndex.current === dropI) { setDragOverIndex(null); return; }
        const reordered = [...sortedFields];
        const [moved] = reordered.splice(dragIndex.current, 1);
        reordered.splice(dropI, 0, moved);
        onChange({ ...section, fields: reordered.map((f, idx) => ({ ...f, order: idx + 1 })) });
        dragIndex.current = null;
        setDragOverIndex(null);
    };
    const handleDragEnd = () => { dragIndex.current = null; setDragOverIndex(null); };

    const hasErrors = Object.keys(sectionErrors).length > 0;

    return (
        <div
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            style={{ marginBottom: '12px', opacity: isDragging ? 0.4 : 1, transition: 'opacity 0.15s' }}
        >
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(v => !v)}
            variant="outlined"
            style={{ border: isDragOver ? '2px solid #3f51b5' : (hasErrors ? '1px solid #f44336' : undefined), backgroundColor: isDragOver ? '#e8eaf6' : 'var(--bg-canvas)', transition: 'border 0.15s, background-color 0.15s' }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <DragIndicatorIcon fontSize="small" style={{ color: '#bbb', cursor: 'grab', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>Section {index + 1}: {section.label || '(Untitled)'}</span>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>({section.fields?.length || 0} fields)</span>
                    {hasErrors && <span style={{ color: 'var(--status-declined)', fontSize: '0.75rem', marginLeft: 'auto' }}>⚠ Fix errors</span>}
                </div>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', flex: 1, marginRight: '12px' }}>
                        <TextField
                            label="Section Label *" value={section.label}
                            onChange={(e) => set('label', e.target.value)}
                            size="small" variant="outlined" fullWidth
                            error={!!sectionErrors.label} helperText={sectionErrors.label}
                        />
                        <TextField
                            label="Order" type="number" value={section.order}
                            onChange={(e) => set('order', parseInt(e.target.value) || 0)}
                            size="small" variant="outlined" fullWidth
                            error={!!sectionErrors.order}
                            helperText={sectionErrors.order || (section.order !== index + 1
                                ? <span>Suggested:<button type="button" onClick={() => set('order', index + 1)} style={{ background: 'none', border: 'none', color: 'var(--rox-lilac-ink)', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}>{index + 1}</button></span>
                                : undefined)}
                        />
                    </div>
                    {showRemove && <IconButton onClick={onRemove} color="secondary" size="small"><DeleteIcon /></IconButton>}
                </div>

                <SectionConfigurations
                    configurations={section.configurations}
                    onChangeConfigs={(cfgs) => set('configurations', cfgs)}
                    configErrors={sectionErrors.configurations || {}}
                    taskType={taskType}
                    sectionTypeWarning={sectionWarnings.sectionType}
                />

                <Divider style={{ marginBottom: '16px' }} />
                <div style={{ marginBottom: '8px', fontWeight: 500 }}>Fields</div>
                {sectionErrors.fields && typeof sectionErrors.fields === 'string' && (
                    <div style={{ color: 'var(--status-declined)', fontSize: '0.85rem', marginBottom: '8px' }}>{sectionErrors.fields}</div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {sortedFields.map((field, i) => (
                        <FieldForm
                            key={field.tempId || field.id || i}
                            field={field}
                            index={i}
                            onChange={(u) => updateField(section.fields.indexOf(field), u)}
                            onRemove={() => removeField(section.fields.indexOf(field))}
                            isDragOver={dragOverIndex === i}
                            onDragStart={(e) => handleDragStart(e, i)}
                            onDragOver={(e) => handleDragOver(e, i)}
                            onDrop={(e) => handleDrop(e, i)}
                            onDragEnd={handleDragEnd}
                            fieldErrors={(sectionErrors.fields_map || {})[section.fields.indexOf(field)] || {}}
                            fieldWarnings={(sectionWarnings.fields_map || {})[section.fields.indexOf(field)] || {}}
                            sectionType={sectionType}
                            taskType={taskType}
                        />
                    ))}
                </div>
                <Button startIcon={<AddIcon />} onClick={addField} color="primary" size="small" style={{ marginTop: '8px' }}>Add Field</Button>
            </AccordionDetails>
        </Accordion>
        </div>
    );
};

const TaskForm = ({ task, onChange, onRemove, index, isDragOver = false, isDragging = false, onDragStart, onDragOver, onDrop, onDragEnd, taskErrors = {}, taskWarnings = {}, collapseKey = 0, expandKey = 0 }) => {
    const set = (key, val) => onChange({ ...task, [key]: val });
    const [expanded, setExpanded] = useState(true);
    useEffect(() => { if (collapseKey > 0) setExpanded(false); }, [collapseKey]);
    useEffect(() => { if (expandKey > 0) setExpanded(true); }, [expandKey]);
    const updateSection = (i, s) => { const arr = [...task.sections]; arr[i] = s; onChange({ ...task, sections: arr }); };
    const addSection = () => onChange({ ...task, sections: [...task.sections, { ...emptySection(), order: task.sections.length + 1 }] });
    const removeSection = (i) => onChange({ ...task, sections: task.sections.filter((_, idx) => idx !== i) });

    const hasErrors = Object.keys(taskErrors).length > 0;

    return (
        <div
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            style={{ marginBottom: '12px', opacity: isDragging ? 0.4 : 1, transition: 'opacity 0.15s' }}
        >
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(v => !v)}
            variant="outlined"
            style={{ border: isDragOver ? '2px solid #4caf50' : (hasErrors ? '1px solid #f44336' : undefined), backgroundColor: isDragOver ? 'var(--badge-approved-bg)' : undefined, transition: 'border 0.15s, background-color 0.15s' }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <DragIndicatorIcon fontSize="small" style={{ color: '#bbb', cursor: 'grab', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>Task {index + 1}: {task.name || '(Untitled)'}</span>
                    {hasErrors && <span style={{ color: 'var(--status-declined)', fontSize: '0.75rem', marginLeft: 'auto' }}>⚠ Fix errors</span>}
                </div>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <IconButton onClick={onRemove} color="secondary" size="small"><DeleteIcon /></IconButton>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <TextField
                        label="Task Name *" value={task.name}
                        onChange={(e) => set('name', e.target.value)}
                        size="small" variant="outlined" fullWidth
                        error={!!taskErrors.name} helperText={taskErrors.name}
                    />
                    <TextField
                        label="Order" type="number" value={task.order}
                        onChange={(e) => set('order', parseInt(e.target.value) || 0)}
                        size="small" variant="outlined" fullWidth
                        error={!!taskErrors.order}
                        helperText={taskErrors.order || (task.order !== index + 1
                            ? <span>Suggested:<button type="button" onClick={() => set('order', index + 1)} style={{ background: 'none', border: 'none', color: 'var(--rox-lilac-ink)', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}>{index + 1}</button></span>
                            : undefined)}
                    />
                    <TextField
                        label="Description" value={task.description}
                        onChange={(e) => set('description', e.target.value)}
                        size="small" variant="outlined" fullWidth
                        style={{ gridColumn: '1 / -1' }}
                    />
                    <FormControl variant="outlined" size="small" fullWidth error={!!taskErrors.requirementLevel}>
                        <InputLabel>Requirement Level</InputLabel>
                        <Select value={task.requirementLevel} onChange={(e) => set('requirementLevel', e.target.value)} label="Requirement Level">
                            {REQUIREMENT_LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                        </Select>
                        {taskErrors.requirementLevel && <FormHelperText>{taskErrors.requirementLevel}</FormHelperText>}
                    </FormControl>
                    <FormControl variant="outlined" size="small" fullWidth error={!!taskErrors.type}>
                        <InputLabel>Task Type</InputLabel>
                        <Select value={task.type} onChange={(e) => set('type', e.target.value)} label="Task Type">
                            {TASK_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                        {taskErrors.type && <FormHelperText>{taskErrors.type}</FormHelperText>}
                    </FormControl>
                </div>
                {task.type !== 'PLAID_DATA_CONFIGURABLE' && (
                    <>
                        <Divider style={{ marginBottom: '16px' }} />
                        <div style={{ marginBottom: '12px', fontWeight: 500 }}>Task Sections</div>
                        {taskErrors.sections && typeof taskErrors.sections === 'string' && (
                            <div style={{ color: 'var(--status-declined)', fontSize: '0.85rem', marginBottom: '8px' }}>{taskErrors.sections}</div>
                        )}
                        {task.sections?.map((section, i) => (
                            <SectionForm
                                key={section.tempId || section.id || i}
                                section={section}
                                index={i}
                                onChange={(u) => updateSection(i, u)}
                                onRemove={() => removeSection(i)}
                                showRemove={task.sections.length > 1}
                                sectionErrors={(taskErrors.sections_map || {})[i] || {}}
                                    sectionWarnings={(taskWarnings.sections_map || {})[i] || {}}
                                    taskType={task.type}
                            />
                        ))}
                        <Button startIcon={<AddIcon />} onClick={addSection} color="primary" size="small">Add Section</Button>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
        </div>
    );
};

const InsertZone = ({ onInsert }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '2px 0', height: '24px', cursor: 'pointer' }}
            onClick={onInsert}
        >
            <div style={{ flex: 1, height: '2px', backgroundColor: hovered ? 'var(--rox-lilac-ink)' : 'transparent', transition: 'background-color 0.15s' }} />
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '22px', height: '22px', borderRadius: '50%',
                backgroundColor: hovered ? 'var(--rox-lilac-ink)' : 'transparent',
                color: hovered ? '#fff' : 'transparent',
                fontSize: '18px', fontWeight: 'bold', lineHeight: 1,
                transition: 'background-color 0.15s, color 0.15s',
                flexShrink: 0,
            }}>+</div>
            <div style={{ flex: 1, height: '2px', backgroundColor: hovered ? 'var(--rox-lilac-ink)' : 'transparent', transition: 'background-color 0.15s' }} />
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────

const cleanData = (obj) => {
    if (Array.isArray(obj)) return obj.map(cleanData);
    if (obj && typeof obj === 'object') {
        const { tempId, ...rest } = obj;
        return Object.keys(rest).reduce((acc, key) => { acc[key] = cleanData(rest[key]); return acc; }, {});
    }
    return obj;
};

const collectFieldNameWarnings = (fields, contextLabel) => {
    const warns = [];
    (fields || []).forEach((f) => {
        if (f.internalFieldType && !FREE_INTERNAL_TYPES.includes(f.internalFieldType)) {
            const expected = INTERNAL_FIELD_TYPE_TO_FIELD_NAME[f.internalFieldType];
            if (expected && f.name && f.name !== expected) {
                warns.push(`${contextLabel} › "${f.label || f.name}": name '${f.name}' (recommended '${expected}' for ${f.internalFieldType})`);
            }
        }
    });
    return warns;
};

const collectAllErrors = (errs, sectionList, taskList) => {
    const list = [];
    const addField = (prefix, fErr) => {
        ['label', 'name', 'order', 'uicomponentType', 'requirementLevel', 'internalFieldType', '_cfgRequired'].forEach(k => {
            if (fErr[k]) list.push(`${prefix}: ${fErr[k]}`);
        });
        Object.entries(fErr.configurations || {}).forEach(([cfgKey, msg]) => {
            if (typeof msg === 'string') list.push(`${prefix} › ${cfgKey}: ${msg}`);
        });
    };
    const addSection = (sPrefix, sErr, fields) => {
        ['label', 'order', 'sectionType'].forEach(k => { if (sErr[k]) list.push(`${sPrefix}: ${sErr[k]}`); });
        if (sErr.fields && typeof sErr.fields === 'string') list.push(`${sPrefix}: ${sErr.fields}`);
        Object.entries(sErr.fields_map || {}).forEach(([fi, fErr]) => {
            const fLabel = fields?.[+fi]?.label || `Field ${+fi + 1}`;
            addField(`${sPrefix} › ${fLabel}`, fErr);
        });
    };
    Object.entries(errs.sections || {}).forEach(([si, sErr]) => {
        const sLabel = sectionList[+si]?.label || `Section ${+si + 1}`;
        addSection(sLabel, sErr, sectionList[+si]?.fields);
    });
    Object.entries(errs.tasks || {}).forEach(([ti, tErr]) => {
        const tLabel = taskList[+ti]?.name || `Task ${+ti + 1}`;
        ['name', 'order', 'type', 'requirementLevel'].forEach(k => { if (tErr[k]) list.push(`${tLabel}: ${tErr[k]}`); });
        if (tErr.sections && typeof tErr.sections === 'string') list.push(`${tLabel}: ${tErr.sections}`);
        Object.entries(tErr.sections_map || {}).forEach(([si, sErr]) => {
            const section = taskList[+ti]?.sections?.[+si];
            const sLabel = section?.label || `Section ${+si + 1}`;
            addSection(`${tLabel} › ${sLabel}`, sErr, section?.fields);
        });
    });
    return list;
};

const collectAllWarnings = (sectionList, taskList) => {
    const warns = [];
    // field-name warnings (existing)
    sectionList.forEach((s, si) => {
        warns.push(...collectFieldNameWarnings(s.fields, `Section "${s.label || si + 1}"`));
    });
    taskList.forEach((t) => {
        (t.sections || []).forEach((s, si) => {
            warns.push(...collectFieldNameWarnings(s.fields, `Task "${t.name}" › Section "${s.label || si + 1}"`));
        });
    });
    // rule-based warnings
    const { ruleWarnings } = runRules(sectionList, taskList);
    warns.push(...flattenIssues(ruleWarnings, sectionList, taskList).map(w => `${w.path}: ${w.message}`));
    return warns;
};

const addTempIds = ({ sectionList = [], taskList = [] }) => ({
    sectionList: sectionList.map(s => ({
        ...s,
        tempId: Date.now() + Math.random(),
        fields: (s.fields || []).map(f => ({ ...f, tempId: Date.now() + Math.random() })),
    })),
    taskList: taskList.map(t => ({
        ...t,
        tempId: Date.now() + Math.random(),
        sections: (t.sections || []).map(s => ({
            ...s,
            tempId: Date.now() + Math.random(),
            fields: (s.fields || []).map(f => ({ ...f, tempId: Date.now() + Math.random() })),
        })),
    })),
});

const DynamicConfigDisplay = ({ dynamicConfig, onCreateConfig, onSubmit, loading }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [sectionList, setSectionList] = useState([emptySection()]);
    const [taskList, setTaskList] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [warnConfirmOpen, setWarnConfirmOpen] = useState(false);
    const [pendingWarnings, setPendingWarnings] = useState([]);
    const [errorConfirmOpen, setErrorConfirmOpen] = useState(false);
    const [pendingErrors, setPendingErrors] = useState([]);
    const [errorConfirmText, setErrorConfirmText] = useState('');
    const [errors, setErrors] = useState({ sections: {}, tasks: {} });
    const [warnings, setWarnings] = useState({ sections: {}, tasks: {} });
    const [collapseKey, setCollapseKey] = useState(0);
    const [expandKey, setExpandKey] = useState(0);
    const [allCollapsed, setAllCollapsed] = useState(false);
    const [inputMode, setInputMode] = useState('form'); // 'form' | 'json'
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [jsonCopied, setJsonCopied] = useState(false);

    const handleSwitchToJson = () => {
        const data = cleanData({ sectionList, taskList });
        setJsonText(JSON.stringify(data, null, 2));
        setJsonError('');
        setInputMode('json');
    };

    const handleSwitchToForm = () => {
        setInputMode('form');
        setJsonError('');
    };

    // Parses the JSON editor text and loads it into the form; returns the lists or null if invalid
    const loadJsonIntoForm = () => {
        try {
            const parsed = JSON.parse(jsonText);
            const { sectionList: sl, taskList: tl } = addTempIds({
                sectionList: Array.isArray(parsed.sectionList) ? parsed.sectionList : [],
                taskList: Array.isArray(parsed.taskList) ? parsed.taskList : [],
            });
            const sections = sl.length > 0 ? sl : [emptySection()];
            setSectionList(sections);
            setTaskList(tl);
            setJsonError('');
            return { sectionList: sections, taskList: tl };
        } catch (e) {
            setJsonError(`Invalid JSON: ${e.message}`);
            return null;
        }
    };

    const handleApplyJson = () => { loadJsonIntoForm(); };

    const handleSaveJson = () => {
        const loaded = loadJsonIntoForm();
        if (!loaded) return;
        const { errs } = validateAll(loaded.sectionList, loaded.taskList);
        const { ruleErrors } = runRules(loaded.sectionList, loaded.taskList);
        startSave(loaded.sectionList, loaded.taskList, mergeIssues(errs, ruleErrors));
    };

    const handleCopyJson = () => {
        const data = cleanData({
            sectionList: dynamicConfig?.sectionList || [],
            taskList: dynamicConfig?.taskList || [],
        });
        const text = JSON.stringify(data, null, 2);
        if (!navigator.clipboard?.writeText) return;
        navigator.clipboard.writeText(text)
            .then(() => {
                setJsonCopied(true);
                setTimeout(() => setJsonCopied(false), 2000);
            })
            .catch(() => {
                setJsonCopied(false);
            });
    };

    const handleToggleAll = () => {
        if (allCollapsed) { setExpandKey(k => k + 1); setAllCollapsed(false); }
        else { setCollapseKey(k => k + 1); setAllCollapsed(true); }
    };

    const [sectionDragging, setSectionDragging] = useState(null);
    const [sectionDragOver, setSectionDragOver] = useState(null);
    const sectionDragIndex = useRef(null);

    const handleSectionDragStart = (e, i) => { sectionDragIndex.current = i; setSectionDragging(i); e.dataTransfer.effectAllowed = 'move'; };
    const handleSectionDragOver = (e, i) => { e.preventDefault(); if (sectionDragIndex.current !== i) setSectionDragOver(i); };
    const handleSectionDrop = (e, dropI, list, setList) => {
        e.preventDefault();
        if (sectionDragIndex.current === null || sectionDragIndex.current === dropI) { setSectionDragOver(null); return; }
        const reordered = [...list];
        const [moved] = reordered.splice(sectionDragIndex.current, 1);
        reordered.splice(dropI, 0, moved);
        setList(reordered.map((s, idx) => ({ ...s, order: idx + 1 })));
        sectionDragIndex.current = null;
        setSectionDragging(null);
        setSectionDragOver(null);
    };
    const handleSectionDragEnd = () => { sectionDragIndex.current = null; setSectionDragging(null); setSectionDragOver(null); };

    const [taskDragging, setTaskDragging] = useState(null);
    const [taskDragOver, setTaskDragOver] = useState(null);
    const taskDragIndex = useRef(null);

    const handleTaskDragStart = (e, i) => { taskDragIndex.current = i; setTaskDragging(i); e.dataTransfer.effectAllowed = 'move'; };
    const handleTaskDragOver = (e, i) => { e.preventDefault(); if (taskDragIndex.current !== i) setTaskDragOver(i); };
    const handleTaskDrop = (e, dropI) => {
        e.preventDefault();
        if (taskDragIndex.current === null || taskDragIndex.current === dropI) { setTaskDragOver(null); return; }
        const reordered = [...taskList];
        const [moved] = reordered.splice(taskDragIndex.current, 1);
        reordered.splice(dropI, 0, moved);
        setTaskList(reordered.map((t, idx) => ({ ...t, order: idx + 1 })));
        taskDragIndex.current = null;
        setTaskDragging(null);
        setTaskDragOver(null);
    };
    const handleTaskDragEnd = () => { taskDragIndex.current = null; setTaskDragging(null); setTaskDragOver(null); };

    const hasConfig = dynamicConfig &&
        ((dynamicConfig.sectionList && dynamicConfig.sectionList.length > 0) ||
            (dynamicConfig.taskList && dynamicConfig.taskList.length > 0));

    useEffect(() => {
        if (dynamicConfig) {
            setSectionList(dynamicConfig.sectionList?.length > 0 ? dynamicConfig.sectionList : [emptySection()]);
            setTaskList(dynamicConfig.taskList || []);
        }
    }, [dynamicConfig]);

    useEffect(() => {
        const { errs } = validateAll(sectionList, taskList);
        const { ruleErrors, ruleWarnings } = runRules(sectionList, taskList);
        setErrors(mergeIssues(errs, ruleErrors));
        setWarnings(ruleWarnings);
    }, [sectionList, taskList]);

    const resetErrors = () => {
        setErrors({ sections: {}, tasks: {} });
        setWarnings({ sections: {}, tasks: {} });
    };

    const handleEdit = () => { resetErrors(); setInputMode('form'); setIsEditing(true); setCollapseKey(k => k + 1); setAllCollapsed(true); };

    const handleCancel = () => {
        resetErrors();
        setIsEditing(false);
        setInputMode('form');
        setJsonError('');
        if (dynamicConfig) {
            setSectionList(dynamicConfig.sectionList?.length > 0 ? dynamicConfig.sectionList : [emptySection()]);
            setTaskList(dynamicConfig.taskList || []);
        }
    };

    // Lists pending confirmation — the JSON save path passes them before the form state updates
    const pendingSaveRef = useRef(null);

    const doSave = () => {
        const data = cleanData(pendingSaveRef.current || { sectionList, taskList });
        pendingSaveRef.current = null;
        onSubmit(data, () => { resetErrors(); setIsEditing(false); });
    };

    const startSave = (sl, tl, errs) => {
        pendingSaveRef.current = { sectionList: sl, taskList: tl };
        const allErrors = collectAllErrors(errs, sl, tl);
        const warns = collectAllWarnings(sl, tl);
        if (allErrors.length > 0) {
            setPendingErrors(allErrors);
            setPendingWarnings(warns);
            setErrorConfirmText('');
            setErrorConfirmOpen(true);
        } else if (warns.length > 0) {
            setPendingWarnings(warns);
            setWarnConfirmOpen(true);
        } else {
            setConfirmOpen(true);
        }
    };

    const handleSave = () => startSave(sectionList, taskList, errors);

    const handleConfirmSave = () => { setConfirmOpen(false); doSave(); };

    const handleWarnConfirmSave = () => {
        setWarnConfirmOpen(false);
        setPendingWarnings([]);
        doSave();
    };

    const handleErrorConfirmSave = () => {
        setErrorConfirmOpen(false);
        setPendingErrors([]);
        setPendingWarnings([]);
        setErrorConfirmText('');
        doSave();
    };

    const handleCreate = () => {
        resetErrors();
        setSectionList([emptySection()]);
        setTaskList([]);
        setInputMode('form');
        setJsonError('');
        setIsEditing(true);
        setCollapseKey(k => k + 1);
        setAllCollapsed(true);
        if (onCreateConfig) onCreateConfig();
    };

    const sectionHeader = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #3f51b5' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h6 style={{ margin: 0, color: 'var(--rox-lilac-ink)' }}>Application Sections</h6>
                {!isEditing && <Chip label={`${(dynamicConfig?.sectionList || []).length} sections`} size="small" color="primary" style={{ fontSize: '0.7rem' }} />}
            </div>
            {isEditing && (
                <Button startIcon={<AddIcon />} onClick={() => setSectionList([...sectionList, { ...emptySection(), order: sectionList.length + 1 }])} color="primary" size="small" variant="outlined">
                    Add Section
                </Button>
            )}
        </div>
    );

    const taskHeader = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #4caf50' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h6 style={{ margin: 0, color: 'var(--status-approved)' }}>Tasks</h6>
                {!isEditing && <Chip label={`${(dynamicConfig?.taskList || []).length} tasks`} size="small" style={{ backgroundColor: 'var(--status-approved)', color: 'white', fontSize: '0.7rem' }} />}
            </div>
            {isEditing && (
                <Button startIcon={<AddIcon />} onClick={() => setTaskList([...taskList, { ...emptyTask(), order: taskList.length + 1 }])} color="primary" size="small" variant="outlined">
                    Add Task
                </Button>
            )}
        </div>
    );

    return (
        <>
        <Card className='p-4 mt-3'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h5 style={{ margin: 0 }}>Dynamic Form Configuration</h5>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {isEditing ? (
                        <>
                            {/* Mode toggle */}
                            <div style={{ display: 'flex', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                                <button
                                    onClick={handleSwitchToForm}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        padding: '5px 12px', border: 'none', cursor: 'pointer',
                                        fontSize: '0.8rem', fontWeight: 500,
                                        backgroundColor: inputMode === 'form' ? 'var(--rox-lilac-ink)' : '#fff',
                                        color: inputMode === 'form' ? '#fff' : '#555',
                                        transition: 'background-color 0.15s',
                                    }}
                                >
                                    <ViewStreamIcon style={{ fontSize: '16px' }} /> Form
                                </button>
                                <button
                                    onClick={handleSwitchToJson}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        padding: '5px 12px', border: 'none', borderLeft: '1px solid #ccc', cursor: 'pointer',
                                        fontSize: '0.8rem', fontWeight: 500,
                                        backgroundColor: inputMode === 'json' ? 'var(--rox-lilac-ink)' : '#fff',
                                        color: inputMode === 'json' ? '#fff' : '#555',
                                        transition: 'background-color 0.15s',
                                    }}
                                >
                                    <CodeIcon style={{ fontSize: '16px' }} /> JSON
                                </button>
                            </div>
                            {inputMode === 'form' && (
                                <Button onClick={handleToggleAll} startIcon={allCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />} size="small">
                                    {allCollapsed ? 'Expand All' : 'Collapse All'}
                                </Button>
                            )}
                            <Button onClick={handleCancel} disabled={loading} startIcon={<CloseIcon />}>Cancel</Button>
                            {inputMode === 'form' ? (
                                <Button variant="contained" color="primary" onClick={handleSave} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>
                                    Save
                                </Button>
                            ) : (
                                <>
                                    <Button variant="outlined" color="primary" onClick={handleApplyJson} startIcon={<ViewStreamIcon />}>
                                        Apply JSON
                                    </Button>
                                    <Button variant="contained" color="primary" onClick={handleSaveJson} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>
                                        Save
                                    </Button>
                                </>
                            )}
                        </>
                    ) : hasConfig ? (
                        <>
                            <Button
                                size="small"
                                startIcon={<FileCopyIcon />}
                                onClick={handleCopyJson}
                                style={{ color: jsonCopied ? 'var(--status-approved)' : undefined }}
                            >
                                {jsonCopied ? 'Copied!' : 'Copy JSON'}
                            </Button>
                            <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEdit}>
                                Edit Configuration
                            </Button>
                        </>
                    ) : (
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
                            Create Dynamic Configuration
                        </Button>
                    )}
                </div>
            </div>

            {!hasConfig && !isEditing ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', backgroundColor: 'var(--bg-canvas)', borderRadius: '8px' }}>
                    <p style={{ color: '#666', marginBottom: '16px' }}>No dynamic configuration has been set up for this portal.</p>
                </div>
            ) : isEditing && inputMode === 'json' ? (
                <div>
                    <p style={{ color: '#666', marginBottom: '8px', fontSize: '0.85rem' }}>
                        Paste or edit the JSON with the structure <code>{'{ "sectionList": [...], "taskList": [...] }'}</code> and click <strong>Apply JSON</strong> to load the configuration into the form, or <strong>Save</strong> to save it directly.
                    </p>
                    <JsonEditor
                        value={jsonText}
                        onChange={(text) => { setJsonText(text); setJsonError(''); }}
                        hasError={!!jsonError}
                        error={jsonError}
                        fullscreenActions={(
                            <>
                                <Button size="small" onClick={handleCancel} disabled={loading} startIcon={<CloseIcon />}>Cancel</Button>
                                <Button size="small" variant="outlined" color="primary" onClick={handleApplyJson} startIcon={<ViewStreamIcon />}>Apply JSON</Button>
                                <Button size="small" variant="contained" color="primary" onClick={handleSaveJson} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>Save</Button>
                            </>
                        )}
                    />
                    {jsonError && (
                        <p style={{ color: 'var(--status-declined)', fontSize: '0.8rem', marginTop: '6px' }}>{jsonError}</p>
                    )}
                </div>
            ) : isEditing ? (
                <>
                    <div style={{ marginBottom: '32px' }}>
                        {sectionHeader}
                        <InsertZone onInsert={() => setSectionList([{ ...emptySection(), order: 1 }, ...sectionList.map((s, idx) => ({ ...s, order: idx + 2 }))])} />
                        {sectionList.map((section, i) => (
                            <React.Fragment key={section.tempId || section.id || i}>
                                <SectionForm
                                    section={section}
                                    index={i}
                                    onChange={(u) => { const arr = [...sectionList]; arr[i] = u; setSectionList(arr); }}
                                    onRemove={() => setSectionList(sectionList.filter((_, idx) => idx !== i))}
                                    showRemove={sectionList.length > 1}
                                    isDragOver={sectionDragOver === i}
                                    isDragging={sectionDragging === i}
                                    onDragStart={(e) => handleSectionDragStart(e, i)}
                                    onDragOver={(e) => handleSectionDragOver(e, i)}
                                    onDrop={(e) => handleSectionDrop(e, i, sectionList, setSectionList)}
                                    onDragEnd={handleSectionDragEnd}
                                    sectionErrors={(errors.sections || {})[i] || {}}
                                    sectionWarnings={(warnings.sections || {})[i] || {}}
                                    collapseKey={collapseKey}
                                    expandKey={expandKey}
                                />
                                <InsertZone onInsert={() => {
                                    const next = [...sectionList];
                                    next.splice(i + 1, 0, { ...emptySection(), order: i + 2 });
                                    setSectionList(next.map((s, idx) => ({ ...s, order: idx + 1 })));
                                }} />
                            </React.Fragment>
                        ))}
                    </div>
                    <div>
                        {taskHeader}
                        {taskList.length === 0 ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>No tasks added. Click "Add Task" to create configurable tasks.</p>
                        ) : (
                            <>
                                <InsertZone onInsert={() => setTaskList([{ ...emptyTask(), order: 1 }, ...taskList.map((t, idx) => ({ ...t, order: idx + 2 }))])} />
                                {taskList.map((task, i) => (
                                    <React.Fragment key={task.tempId || task.id || i}>
                                        <TaskForm
                                            task={task}
                                            index={i}
                                            onChange={(u) => { const arr = [...taskList]; arr[i] = u; setTaskList(arr); }}
                                            onRemove={() => setTaskList(taskList.filter((_, idx) => idx !== i))}
                                            isDragOver={taskDragOver === i}
                                            isDragging={taskDragging === i}
                                            onDragStart={(e) => handleTaskDragStart(e, i)}
                                            onDragOver={(e) => handleTaskDragOver(e, i)}
                                            onDrop={(e) => handleTaskDrop(e, i)}
                                            onDragEnd={handleTaskDragEnd}
                                            taskErrors={(errors.tasks || {})[i] || {}}
                                            taskWarnings={(warnings.tasks || {})[i] || {}}
                                            collapseKey={collapseKey}
                                            expandKey={expandKey}
                                        />
                                        <InsertZone onInsert={() => {
                                            const next = [...taskList];
                                            next.splice(i + 1, 0, { ...emptyTask(), order: i + 2 });
                                            setTaskList(next.map((t, idx) => ({ ...t, order: idx + 1 })));
                                        }} />
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {dynamicConfig.sectionList && dynamicConfig.sectionList.length > 0 && (
                        <div style={{ marginBottom: '32px' }}>
                            {sectionHeader}
                            {[...dynamicConfig.sectionList].sort((a, b) => a.order - b.order).map((section, i) => (
                                <SectionDisplay key={section.id} section={section} index={i} />
                            ))}
                        </div>
                    )}
                    {dynamicConfig.taskList && dynamicConfig.taskList.length > 0 && (
                        <div>
                            {taskHeader}
                            {[...dynamicConfig.taskList].sort((a, b) => a.order - b.order).map((task) => (
                                <TaskDisplay key={task.id} task={task} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </Card>

        <Dialog open={errorConfirmOpen} fullWidth maxWidth="sm">
            <DialogTitle style={{ color: 'var(--status-declined)' }}>Configuration errors</DialogTitle>
            <DialogContent>
                <DialogContentText style={{ marginBottom: '8px' }}>
                    This configuration has the following errors. To save anyway, type <strong>SAVE</strong> in the field below.
                </DialogContentText>
                <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                    {pendingErrors.map((e, i) => (
                        <li key={i} style={{ fontSize: '0.82rem', color: 'var(--status-declined)', marginBottom: '4px' }}>{e}</li>
                    ))}
                </ul>
                {pendingWarnings.length > 0 && <>
                    <DialogContentText style={{ marginBottom: '6px', marginTop: '8px', color: '#e65100', fontSize: '0.85rem' }}>
                        This configuration also has the following warnings:
                    </DialogContentText>
                    <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                        {pendingWarnings.map((w, i) => (
                            <li key={i} style={{ fontSize: '0.82rem', color: '#e65100', marginBottom: '4px' }}>{w}</li>
                        ))}
                    </ul>
                </>}
                <TextField
                    value={errorConfirmText}
                    onChange={(e) => setErrorConfirmText(e.target.value)}
                    placeholder='Type "SAVE" to confirm'
                    size="small"
                    fullWidth
                    error={errorConfirmText.length > 0 && errorConfirmText !== 'SAVE'}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setErrorConfirmOpen(false)} disabled={loading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleErrorConfirmSave}
                    disabled={errorConfirmText !== 'SAVE' || loading}
                    style={{ backgroundColor: errorConfirmText === 'SAVE' ? 'var(--status-declined)' : undefined, color: errorConfirmText === 'SAVE' ? '#fff' : undefined }}
                    startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                    Save anyway
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={warnConfirmOpen} fullWidth maxWidth="sm">
            <DialogTitle style={{ color: '#e65100' }}>⚠ Warnings before saving</DialogTitle>
            <DialogContent>
                <DialogContentText style={{ marginBottom: '12px' }}>
                    The configuration has the following warnings. You can save anyway or go back and fix them.
                </DialogContentText>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {pendingWarnings.map((w, i) => (
                        <li key={i} style={{ fontSize: '0.85rem', color: '#555', marginBottom: '6px' }}>{w}</li>
                    ))}
                </ul>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setWarnConfirmOpen(false)} disabled={loading}>Go back and fix</Button>
                <Button variant="contained" onClick={handleWarnConfirmSave} disabled={loading}
                    style={{ backgroundColor: '#e65100', color: '#fff' }}
                    startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>
                    Save anyway
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={confirmOpen} fullWidth maxWidth="xs">
            <DialogTitle>Save Dynamic Configuration</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to save these changes? This will overwrite the current dynamic form configuration.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleConfirmSave} disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default DynamicConfigDisplay;
